import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress, Grid, Typography } from '@material-ui/core'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { defer, firstValueFrom, Observable, of } from 'rxjs'
import { catchError } from 'rxjs/operators'

import { CartFragment } from '../../../graphql/magento'
import { requiredAddressFieldValues } from '../../../lib/cart/address'
import { defaultBillingAddressValues } from '../../../lib/cart/billing'
import { CardExpiredError, CardTypeInvalidError } from '../../../lib/cart/errors'
import { useDefaultCountryCode } from '../../../lib/cart/hooks/useDefaultCountryCode'
import {
  assertValidCardExpiration,
  assertValidCardType,
  availableAccountCredit,
  codeFromCartSelectedPaymentMethod,
  daysAccountOverdue,
  defaultPaymentDetailsValues,
  isPaymentError,
  requiredCreditCardFieldValues,
  setErrorStateOnFields,
} from '../../../lib/cart/payment'
import { isBillingSameAsShipping } from '../../../lib/cart/shipping'
import {
  SavedCreditCardInvalidError,
  SelectedSavedCreditCardResult,
  selectedSavedCreditCardValues$,
} from '../../../lib/cart/state/credit-card-field-values'
import {
  CustomerAccountInfoFromQuery,
  CustomerAddressFromQuery,
  FormValues,
  ValidFormValues,
} from '../../../lib/cart/types'
import { cardTypeFromCardNumber } from '../../../lib/m2/cybersource'
import { useTestKitProducts } from '../../../lib/products/hooks/useTestKitProducts'
import { assertRequiredFields, RequiredFieldsMissingError } from '../../../utils/assertTools'
import { testKitDelayedBilling } from '../../../utils/buy/paymentTools'
import { logAndCaptureException } from '../../../utils/errorTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { mapRight } from '../../../utils/rx/operators'
import ApplicationErrorMessage from '../../ApplicationErrorMessage'
import ErrorUpdatingCartMessage from '../../ErrorUpdatingCartMessage'
import { GridButton } from '../shared/GridButton'
import BillingDetails from './BillingDetails'
import PaymentForm from './PaymentForm'
import PaymentMethods from './PaymentMethods'
import PurchaseOrderForm from './PurchaseOrderForm'

type ValidationError =
  | CardTypeInvalidError
  | RequiredFieldsMissingError
  | SavedCreditCardInvalidError
  | Error

const calculatedCreditCardValues = ({
  selectedSavedCreditCardId,
  cardNumber,
}: FormValues.CreditCard): Pick<
  Partial<ValidFormValues.CreditCard>,
  'cardType' | 'subscriptionId'
> =>
  selectedSavedCreditCardId || !cardNumber
    ? {}
    : { cardType: cardTypeFromCardNumber(cardNumber), subscriptionId: 'new' }

const calculatedOrSelectedSavedCreditCardValues$ = (
  formValues: FormValues.PaymentDetails,
): Observable<
  | SelectedSavedCreditCardResult
  | {
      _tag: 'Right'
      data: Pick<Partial<ValidFormValues.CreditCard>, 'cardType' | 'subscriptionId'>
    }
> => {
  const { selectedSavedCreditCardId } = formValues as
    | FormValues.CreditCard
    | FormValues.SavedCreditCard
  return selectedSavedCreditCardId
    ? selectedSavedCreditCardValues$(selectedSavedCreditCardId)
    : of({ _tag: 'Right', data: calculatedCreditCardValues(formValues as FormValues.CreditCard) })
}

const validateCreditCardFormValues$ = (
  formValues: FormValues.PaymentDetails &
    Pick<ValidFormValues.PaymentDetails, 'billingSameAsShipping'>,
): Observable<
  | {
      _tag: 'Left'
      error: ValidationError
    }
  | { _tag: 'Right'; data: ValidFormValues.PaymentDetails }
> =>
  calculatedOrSelectedSavedCreditCardValues$(formValues).pipe(
    mapRight(({ data: calculatedOrSelectCreditCardValues }) => {
      const partialShim: Partial<ValidFormValues.CreditCard> = {}
      const creditCardValues = {
        // shim fields used in credit card to undefined for TS
        ...partialShim,
        ...formValues,
        ...calculatedOrSelectCreditCardValues,
        code: 'paradoxlabs_cybersource' as const,
      }

      assertValidCardExpiration(creditCardValues)
      assertValidCardType(creditCardValues)
      assertRequiredFields(creditCardValues, [
        ...requiredAddressFieldValues,
        ...requiredCreditCardFieldValues,
      ])

      return { _tag: 'Right' as const, data: creditCardValues }
    }),
    catchError(coerceCaughtToLeft),
  )

const validatePurchaseOrderFormValues$ = (
  formValues: FormValues.PaymentDetails &
    Pick<ValidFormValues.PaymentDetails, 'billingSameAsShipping'>,
): Observable<
  | {
      _tag: 'Left'
      error: ValidationError
    }
  | { _tag: 'Right'; data: ValidFormValues.PaymentDetails }
> =>
  defer(() => {
    const partialShim: Partial<ValidFormValues.PurchaseOrder> = {}
    const purchaseOrderValues = {
      // shim fields used in purchase order to undefined for TS
      ...partialShim,
      ...formValues,
      code: 'purchaseorder' as const,
    }

    assertRequiredFields(purchaseOrderValues, requiredAddressFieldValues)
    return of({ _tag: 'Right' as const, data: purchaseOrderValues })
  }).pipe(catchError(coerceCaughtToLeft))

export interface PaymentDetailsProps {
  accountBalance: CustomerAccountInfoFromQuery
  cart: CartFragment
  className?: string
  defaultBillingAddress?: CustomerAddressFromQuery
  error?: Error
  onSubmit: (values: ValidFormValues.PaymentDetails) => Promise<void>
  submitting: boolean
}

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  accountBalance,
  cart,
  className,
  defaultBillingAddress,
  error,
  onSubmit,
  submitting,
}) => {
  const [poCreditLimitError, setPoCreditLimitError] = useState(false)
  const [poDaysOverdueError, setPoDaysOverdueError] = useState(false)
  const [validationError, setValidationError] = useState<ValidationError | undefined>()
  const formErrorsAlertRef = useRef<HTMLDivElement>(null)
  const {
    selected_payment_method: selectedPaymentMethod,
    prices,
    billing_address: billingAddress,
    shipping_addresses: [shippingAddress],
  } = cart
  const availableCredit = availableAccountCredit(accountBalance)
  const daysOverdue = daysAccountOverdue(accountBalance)
  const showPurchaseOrderOption = !!accountBalance?.terms
  const defaultCountryCode = useDefaultCountryCode()
  const { isTestKitSku } = useTestKitProducts()

  const delayedBillingRequired = testKitDelayedBilling({ cart, isTestKitSku })

  const [billingSameAsShipping, setBillingSameAsShipping] = useState(
    () => isBillingSameAsShipping(cart) ?? false,
  )

  const defaultAddressValues = useMemo(
    () =>
      defaultBillingAddressValues({
        billingAddress,
        billingSameAsShipping,
        defaultBillingAddress,
        defaultCountryCode,
        shippingAddress,
      }),
    [
      billingAddress,
      billingSameAsShipping,
      defaultBillingAddress,
      defaultCountryCode,
      shippingAddress,
    ],
  )

  const defaultPaymentMethodCode = codeFromCartSelectedPaymentMethod({
    showPurchaseOrderOption,
    selectedPaymentMethod,
  })

  const methods = useForm<FormValues.PaymentDetails>({
    mode: 'onBlur',
    defaultValues: defaultPaymentDetailsValues({
      billingAddress: defaultAddressValues,
      paymentMethodCode: defaultPaymentMethodCode,
      selectedPaymentMethod: selectedPaymentMethod,
    }),
  })

  const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethodCode)

  const hasFormErrors = useMemo(
    () => Object.keys(methods.formState.errors).length > 0,
    [methods.formState],
  )

  const scrollErrorsRefIntoView = () => {
    const formErrorsEl = formErrorsAlertRef.current
    if (formErrorsEl) {
      formErrorsEl.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault()
    }
    setValidationError(undefined)

    const formValues = methods.getValues()

    const isValid = await methods.trigger()

    if (!isValid) {
      scrollErrorsRefIntoView()
      return
    }

    const result = await firstValueFrom(
      paymentMethod === 'purchaseorder'
        ? validatePurchaseOrderFormValues$({ ...formValues, billingSameAsShipping })
        : validateCreditCardFormValues$({ ...formValues, billingSameAsShipping }),
    )

    if (result._tag === 'Left') {
      logAndCaptureException(result.error)
      setValidationError(result.error)
      scrollErrorsRefIntoView()
      return
    }

    await onSubmit(result.data)
  }

  useEffect(() => {
    if (error) {
      setErrorStateOnFields({
        error,
        getValues: methods.getValues,
        selectedPaymentMethod: paymentMethod,
        setError: methods.setError,
      })
      scrollErrorsRefIntoView()
    }
  }, [error, methods.getValues, methods.setError, paymentMethod])

  useEffect(() => {
    if (!showPurchaseOrderOption) {
      setPaymentMethod('paradoxlabs_cybersource')
    }
  }, [showPurchaseOrderOption])

  useEffect(() => {
    setPoCreditLimitError(
      paymentMethod === 'purchaseorder' && availableCredit < (prices?.grand_total?.value || 0),
    )
  }, [availableCredit, paymentMethod, prices?.grand_total?.value])

  useEffect(() => {
    setPoDaysOverdueError(paymentMethod === 'purchaseorder' && daysOverdue > 0)
  }, [daysOverdue, paymentMethod])

  return (
    <Grid
      alignItems="center"
      justifyContent="center"
      className={className}
      container
      item
      xs={12}
      component="form"
      noValidate
      onSubmit={handleSubmit}
    >
      <Grid item xs={12}>
        <Typography variant="h5">PAYMENT METHOD</Typography>
      </Grid>
      <PaymentMethods
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        showPaymentMethods={showPurchaseOrderOption}
        submitting={submitting}
      />
      <Grid ref={formErrorsAlertRef} item xs={12}>
        {(hasFormErrors || (error && isPaymentError(error))) && (
          <Alert type="error">
            Invalid payment details. Please check the highlighted fields for errors.
          </Alert>
        )}
        {error && !isPaymentError(error) && (
          <Alert type="error">
            <ErrorUpdatingCartMessage />
          </Alert>
        )}
        {!validationError ? null : validationError instanceof SavedCreditCardInvalidError ? (
          <Alert type="error">
            Oops! The selected credit card is missing required information. Please select a
            different payment method.
          </Alert>
        ) : validationError instanceof CardExpiredError ? (
          <Alert type="error">
            Sorry! That card is expired. Please select a different payment method.
          </Alert>
        ) : validationError instanceof CardTypeInvalidError ? (
          <Alert type="error">
            Sorry! That card type is not supported. Please select a different payment method.
          </Alert>
        ) : (
          <Alert type="error">
            <ApplicationErrorMessage />
          </Alert>
        )}
      </Grid>
      <FormProvider {...methods}>
        <PaymentForm
          paymentMethod={paymentMethod}
          delayedBillingRequired={delayedBillingRequired}
        />
        <PurchaseOrderForm
          accountBalance={accountBalance}
          availableCredit={availableCredit}
          daysOverdue={daysOverdue}
          paymentMethod={paymentMethod}
          poCreditLimitError={poCreditLimitError}
          poDaysOverdueError={poDaysOverdueError}
        />
        <BillingDetails
          billingSameAsShipping={billingSameAsShipping}
          defaultAddressValues={defaultAddressValues}
          setBillingSameAsShipping={setBillingSameAsShipping}
        />
      </FormProvider>
      <GridButton
        onClick={handleSubmit}
        type="submit"
        id="continue-to-review-button"
        disabled={poCreditLimitError || poDaysOverdueError || submitting}
        data-cy="go-to-review"
      >
        {submitting ? <CircularProgress size={24} color="inherit" /> : 'CONTINUE TO REVIEW'}
      </GridButton>
    </Grid>
  )
}

export default PaymentDetails
